import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { company } from '../mock';

const lastUpdated = new Date().toLocaleDateString('en-NZ', {
  year: 'numeric',
  month: 'long',
});

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl md:text-2xl font-semibold text-stone-900">
        {title}
      </h2>
      <div className="mt-3 space-y-4 text-stone-700 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function PrivacyContent() {
  return (
    <>
      <p>
        Ask Away Accounting (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;)
        is committed to protecting your personal information in accordance with the
        <strong> New Zealand Privacy Act 2020</strong> and the thirteen Information
        Privacy Principles (IPPs). This policy explains what information we collect,
        how we use it, and the choices you have.
      </p>

      <Section title="1. Information we collect">
        <p>We may collect the following types of personal information:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong>Contact details</strong> — your name, email address, phone number
            and postal address.
          </li>
          <li>
            <strong>Business information</strong> — entity name, IRD number, GST
            number, NZBN, and details about your business activities.
          </li>
          <li>
            <strong>Financial information</strong> — bank statements, invoices,
            receipts, payroll data, livestock records and any other documentation
            required to complete the work you have engaged us for.
          </li>
          <li>
            <strong>Website data</strong> — limited technical information such as IP
            address and pages visited, gathered through standard web analytics.
          </li>
        </ul>
      </Section>

      <Section title="2. How we collect your information">
        <p>
          We collect information directly from you (for example through our contact
          form, by email, by phone or in person), and where authorised, from third
          parties such as Inland Revenue (IRD), your bank, your previous accountant
          or your software provider (Xero, MYOB).
        </p>
      </Section>

      <Section title="3. How we use your information">
        <p>We use your personal information to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>provide the accounting, tax and advisory services you have engaged us for;</li>
          <li>communicate with Inland Revenue (IRD) on your behalf as your tax agent;</li>
          <li>respond to enquiries and provide quotes;</li>
          <li>meet our legal and professional obligations (including record-keeping under the Tax Administration Act 1994 and the Companies Act 1993);</li>
          <li>improve our services.</li>
        </ul>
        <p>
          We do not sell your information, and we do not use it for marketing
          purposes without your consent.
        </p>
      </Section>

      <Section title="4. Sharing your information">
        <p>
          We may share your information with third parties only where necessary to
          deliver our services or where required by law, including:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Inland Revenue (IRD) and other NZ government agencies;</li>
          <li>cloud accounting providers (Xero, MYOB) used to process your data;</li>
          <li>banks and financial institutions, where authorised by you;</li>
          <li>professional advisers (e.g. lawyers, auditors) where relevant to your engagement;</li>
          <li>law enforcement or regulators where legally required.</li>
        </ul>
        <p>
          Some of these providers may store data on servers located outside New
          Zealand (for example, Xero hosts data within Australia). We take
          reasonable steps to ensure your information is protected to a standard
          comparable to the NZ Privacy Act.
        </p>
      </Section>

      <Section title="5. Storage and security">
        <p>
          We store information in secure cloud systems and locked physical files.
          We use reasonable safeguards (passwords, multi-factor authentication,
          access controls) to protect your information from loss, misuse or
          unauthorised access. Records are retained for at least seven years to
          meet our obligations under the Tax Administration Act 1994.
        </p>
      </Section>

      <Section title="6. Your rights">
        <p>Under the Privacy Act 2020 you have the right to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>request access to personal information we hold about you;</li>
          <li>request correction of information that is inaccurate or incomplete;</li>
          <li>withdraw your consent at any time (subject to legal obligations).</li>
        </ul>
        <p>
          To exercise these rights, please email{' '}
          <a className="text-[#0b6b5b] font-medium" href={`mailto:${company.email}`}>
            {company.email}
          </a>{' '}
          or write to {company.address}. We will respond within 20 working days as
          required by the Act.
        </p>
      </Section>

      <Section title="7. Cookies">
        <p>
          Our website uses minimal cookies and analytics to understand how visitors
          use the site. You can disable cookies in your browser at any time, though
          some features may not work as intended.
        </p>
      </Section>

      <Section title="8. Complaints">
        <p>
          If you have a concern about how we have handled your personal
          information, please contact us first at{' '}
          <a className="text-[#0b6b5b] font-medium" href={`mailto:${company.email}`}>
            {company.email}
          </a>
          . If you are not satisfied with our response, you may contact the{' '}
          <a
            className="text-[#0b6b5b] font-medium"
            href="https://www.privacy.org.nz"
            target="_blank"
            rel="noopener noreferrer"
          >
            Office of the Privacy Commissioner
          </a>{' '}
          (privacy.org.nz, freephone 0800 803 909).
        </p>
      </Section>

      <Section title="9. Changes to this policy">
        <p>
          We may update this policy from time to time. The most recent version will
          always be available on this page.
        </p>
      </Section>

      <p className="mt-8 text-sm text-stone-500 border-t border-stone-200 pt-5">
        This policy is provided as a general template that reflects common NZ
        accounting practice. Please review with your legal adviser before relying
        on it.
      </p>
    </>
  );
}

function TermsContent() {
  return (
    <>
      <p>
        These Terms of Use govern your use of the Ask Away Accounting website
        (askawayaccounting.co.nz) and any preliminary communication you have with
        us through it. They are governed by the laws of New Zealand. By using this
        website you agree to these terms.
      </p>

      <Section title="1. About us">
        <p>
          Ask Away Accounting is an accounting practice based at {company.address},
          New Zealand. You can contact us on {company.phone} or{' '}
          <a className="text-[#0b6b5b] font-medium" href={`mailto:${company.email}`}>
            {company.email}
          </a>
          .
        </p>
      </Section>

      <Section title="2. Information on this website">
        <p>
          The information provided on this website is general in nature and does
          <strong> not constitute</strong> tax, financial, legal or accounting
          advice for your specific circumstances. You should not rely on it as
          such. Before acting on any information, please contact us for advice
          tailored to your situation.
        </p>
        <p>
          We take care to keep the website accurate and up to date but we do not
          guarantee that all content is current, complete or error free.
        </p>
      </Section>

      <Section title="3. Engagement and scope of services">
        <p>
          Use of this website, or sending an enquiry through our contact form,
          does <strong>not</strong> by itself create a client relationship. A
          formal engagement letter setting out the scope of work, fees and
          responsibilities will be issued and signed before we begin any
          chargeable work on your behalf, in line with the standards of Chartered
          Accountants Australia and New Zealand (CA ANZ).
        </p>
      </Section>

      <Section title="4. Our obligations">
        <p>
          When engaged, we will perform the agreed services with reasonable care
          and skill in accordance with applicable NZ professional standards,
          including the Tax Administration Act 1994, the Income Tax Act 2007 and
          IRD tax agent requirements.
        </p>
      </Section>

      <Section title="5. Your obligations">
        <p>You agree to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>provide complete, accurate and timely information;</li>
          <li>respond to our requests for information promptly;</li>
          <li>review and approve documents we prepare on your behalf;</li>
          <li>retain your own copies of records as required by IRD (generally seven years).</li>
        </ul>
        <p>
          We are entitled to rely on the information you provide and are not
          responsible for inaccuracies arising from incorrect or incomplete
          information.
        </p>
      </Section>

      <Section title="6. Fees and payment">
        <p>
          Our fees will be set out in your engagement letter or quoted before work
          begins. Invoices are payable within the terms stated on the invoice
          (typically 14 days). Late payment may incur interest and recovery costs.
          GST is added where applicable.
        </p>
      </Section>

      <Section title="7. Confidentiality">
        <p>
          We treat all client information as confidential and will not disclose it
          to third parties except as required by law, with your authorisation, or
          as set out in our Privacy Policy.
        </p>
      </Section>

      <Section title="8. Intellectual property">
        <p>
          All content on this website (including text, graphics, layout and the
          Ask Away Accounting name and logo) is owned by or licensed to us. You
          may view and print pages for personal, non-commercial use, but you may
          not reproduce, modify or republish any content without our written
          consent.
        </p>
      </Section>

      <Section title="9. Limitation of liability">
        <p>
          To the maximum extent permitted by law, our liability to you (whether in
          contract, tort or otherwise) arising from or in connection with this
          website or any engagement is limited to the fees paid by you to us for
          the relevant service in the 12 months prior to the matter giving rise to
          the claim.
        </p>
        <p>
          We exclude liability for indirect, consequential or special loss
          (including loss of profits, loss of business, loss of goodwill or loss
          of data). Nothing in these terms limits any rights you may have under
          the Consumer Guarantees Act 1993 where you acquire services as a
          consumer (other than for the purposes of a business).
        </p>
      </Section>

      <Section title="10. External links">
        <p>
          This website may link to third party websites (for example IRD, Xero,
          MYOB). We are not responsible for the content, accuracy or privacy
          practices of those sites.
        </p>
      </Section>

      <Section title="11. Governing law">
        <p>
          These terms are governed by New Zealand law. Any dispute arising under
          them will be subject to the non-exclusive jurisdiction of the New
          Zealand courts.
        </p>
      </Section>

      <Section title="12. Changes">
        <p>
          We may update these terms at any time. The latest version will always
          be available on this page.
        </p>
      </Section>

      <p className="mt-8 text-sm text-stone-500 border-t border-stone-200 pt-5">
        These terms are provided as a general template reflecting common NZ
        accounting practice. Please review with your legal adviser before relying
        on them.
      </p>
    </>
  );
}

export default function Legal({ kind }) {
  const isPrivacy = kind === 'privacy';
  const title = isPrivacy ? 'Privacy Policy' : 'Terms of Use';

  return (
    <>
      <Header />
      <main className="pt-32 pb-20 min-h-[70vh] bg-[#fbfaf5]">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#0b6b5b] font-medium hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="brand-divider mt-6 mb-4" />
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-stone-900">
            {title}
          </h1>
          <p className="mt-3 text-stone-500 text-sm">
            Last updated: {lastUpdated} &middot; Governed by the laws of New Zealand
          </p>
          <div className="mt-2 text-stone-600">
            {isPrivacy ? <PrivacyContent /> : <TermsContent />}
          </div>
        </div>
      </main>
      <Footer />
      <div className="lg:hidden h-14" aria-hidden="true" />
    </>
  );
}
